import React, { useState } from "react";
import { Select, Card } from "antd";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Area,
  AreaChart,
} from "recharts";
import StatCard from "./cards/stat-card/StatCard";
import { ProductOutlined } from "@ant-design/icons";
import { useGetMFIDashboardValues } from "../api/agronomy";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

const formatNumber = (num: number) => {
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1) + "B";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  if (num >= 100) return (num / 100).toFixed(1) + "H";
  return num.toString();
};

const ContractDashboard = ({ contractData }: { contractData: any[] }) => {
  const [tableIndex, setTableIndex] = useState<number>(0);
  const { data: bankDashboardValues } = useGetMFIDashboardValues();

  const navigate = useNavigate();
  // Extract unique values for filters
  const uniqueStatuses = [
    "All",
    ...Array.from(new Set(contractData.map((c) => c.contractStatus))),
  ];
  const uniqueLoanStatuses = [
    "All",
    ...Array.from(new Set(contractData.map((c) => c.loanStatus))),
  ];
  const uniqueNames = [
    "All",
    ...Array.from(new Set(contractData.map((c) => c.fullName))),
  ];

  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedLoanStatus, setSelectedLoanStatus] = useState("All");
  const [selectedName, setSelectedName] = useState("All");
  const [selectedDate, setSelectedDate] = useState(null);

  const filteredContracts = contractData.filter(
    (c) =>
      (selectedStatus === "All" || c.contractStatus === selectedStatus) &&
      (selectedLoanStatus === "All" || c.loanStatus === selectedLoanStatus) &&
      (selectedName === "All" || c.fullName === selectedName) &&
      (!selectedDate || c.contractDate === selectedDate),
  );

  const topContracts = filteredContracts.slice(0, 20);
  const chartData = topContracts.map((c) => ({
    name: c.contractNumber,
    contractPrice: c.contractPrice,
    inputCredit: c.inputCredit,
  }));

  const lineData = topContracts.map((c) => ({
    name: c.contractNumber,
    price: c.contractPrice,
    inputCredit: c.inputCredit,
  }));
  const barData = topContracts.map((c) => ({
    name: c.contractNumber,
    inputCredit: c.inputCredit,
  }));

  const contractStatusCounts = topContracts.reduce(
    (acc, c) => {
      const status = c.contractStatus === "CREATED" ? "UNSIGNED" : "SIGNED";
      acc[status] = (acc[status] || 0) + c.quantity;
      return acc;
    },
    { SIGNED: 0, UNSIGNED: 0 },
  );

  const doughnutData = [
    { name: "SIGNED", value: contractStatusCounts.SIGNED },
    { name: "UNSIGNED", value: contractStatusCounts.UNSIGNED },
  ];

  const loanStatusCounts = topContracts.reduce(
    (acc, c) => {
      acc[c.loanStatus] = (acc[c.loanStatus] || 0) + c.quantity;
      return acc;
    },
    { PENDING: 0, APPROVED: 0 },
  );

  const pieData = [
    { name: "PENDING", value: loanStatusCounts.PENDING },
    { name: "APPROVED", value: loanStatusCounts.APPROVED },
  ];

  const COLORS = ["#F59E0B", "#4F46E5"];

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
    name,
  }: {
    cx: number;
    cy: number;
    midAngle: number;
    innerRadius: number;
    outerRadius: number;
    percent: number;
    index: number;
    name: string;
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180);
    const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
      >
        {`${name} ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <main className="flex flex-col gap-6 bg-white p-6">
      <div className="flex flex-wrap gap-4 justify-center w-full">
        <div
          onClick={() => {
            setTableIndex(2);
            navigate("/contracts");
          }}
          style={{ cursor: "pointer" }}
          className="flex-1"
        >
          <StatCard
            className="w-[288px]"
            title="Number of Contracts"
            value={bankDashboardValues?.data?.data?.NoOfContracts || 0}
            bgColor="#000"
            icon={
              <svg
                fill="#000000"
                width="64px"
                height="64px"
                viewBox="0 0 32 32"
                fillRule="evenodd"
                clipRule={"evenodd"}
                strokeLinejoin="round"
                strokeMiterlimit={2}
                version="1.1"
                xmlSpace="preserve"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
              >
                {/* SVG Paths */}
              </svg>
            }
          />
        </div>
        <div
          onClick={() => {
            setTableIndex(2);
            navigate("/customers");
          }}
          style={{ cursor: "pointer" }}
          className="flex-1"
        >
          <StatCard
            className="w-[288px]"
            title="Number of Farmers"
            value={bankDashboardValues?.data?.data?.NoOfFarmers || 0}
            bgColor="#49B896"
            icon={
              <svg
                width="33"
                height="32"
                viewBox="0 0 33 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* SVG Paths */}
              </svg>
            }
          />
        </div>
        <div
          onClick={() => {
            setTableIndex(2);
            navigate("/");
          }}
          style={{ cursor: "pointer" }}
          className="flex-1"
        >
          <StatCard
            className="w-[288px]"
            title="No. of Processed Loans"
            value={bankDashboardValues?.data?.data?.LoanProcessed || 0}
            bgColor="#CE7270"
            icon={
              <svg
                width="30"
                height="31"
                viewBox="0 0 30 31"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* SVG Paths */}
              </svg>
            }
          />
        </div>
        <div
          onClick={() => setTableIndex(2)}
          style={{ cursor: "pointer" }}
          className="flex-1"
        >
          <StatCard
            className="w-[288px]"
            title="No. of Pending Loans"
            value={bankDashboardValues?.data?.data?.PendingLoans || 0}
            bgColor="#000"
            icon={
              <svg
                width="64px"
                height="64px"
                viewBox="0 0 1024 1024"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                fill="#000000"
              >
                {/* SVG Paths */}
              </svg>
            }
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          {
            label: "Contract Status",
            options: uniqueStatuses,
            state: selectedStatus,
            setter: setSelectedStatus,
          },
          {
            label: "Loan Status",
            options: uniqueLoanStatuses,
            state: selectedLoanStatus,
            setter: setSelectedLoanStatus,
          },
          {
            label: "Customer Name",
            options: uniqueNames,
            state: selectedName,
            setter: setSelectedName,
          },
        ].map(({ label, options, state, setter }) => (
          <div key={label}>
            <label className="text-sm font-medium text-gray-700">{label}</label>
            <Select className="w-full" value={state} onChange={setter}>
              {options.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </div>
        ))}
      </div>
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-transparent">
          <h3 className="text-gray-500 mb-4 text-lg font-semibold px-4 pt-2">
            Contract Price Trend
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={lineData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={formatNumber} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="price" stroke="#8884d8" />
                <Line type="monotone" dataKey="inputCredit" stroke="#82ca9d" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="bg-gray-50">
          <h3 className="text-gray-500 mb-4 text-lg font-semibold">
            Contract Status
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={doughnutData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {doughnutData.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card className="bg-gray-50">
          <h3 className="text-gray-500 mb-4 text-lg font-semibold">
            Contract Input Credit Overview
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={barData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.5} />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="inputCredit"
                fill="#F59E0B"
                stroke="#D97706"
                strokeWidth={2}
              />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="bg-gray-50">
          <h3 className="text-gray-500 mb-4 text-lg font-semibold">
            Loan Status Overview
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
              >
                {pieData.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </section>
    </main>
  );
};

export default ContractDashboard;
